import { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  DialogTitle,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { incidentsApi } from '../../../../functions/IncidentsApi';
import { toast } from 'react-toastify';
import NoteForm from './NoteForm';
import EditIcon from '@mui/icons-material/Edit';
import AuthContext from '../../../../context/AuthContext';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    background: '#2121214c',
  },
  '& .MuiPaper-root': {
    maxWidth: 800,
    width: 800,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    borderTop: 'none',
    scrollBehavior: 'smooth',
  },
  '& .MuiDialogActions-root': {
    '& .actionButton': {
      borderColor: '#212121',
      color: '#212121',

      '&.MuiButton-contained': {
        background: '#212121',
        color: 'white',
        '&:disabled': {
          background: 'gray',
        },
      },
      '&.success': {
        '&.MuiButton-contained': {
          background: '#75ec9f',
          color: '#000000dd',
        },
      },
    },
  },
}));

export default function CreateNote({ open, setOpen }) {
  const { user } = useContext(AuthContext);
  const [selectedEditNoteId, setSelectedEditNoteId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [fetchNote, setFetchNote] = useState(false);
  useEffect(() => {
    const fetchNote = async () => {
      try {
        setFetchNote(true);
        const response = await incidentsApi.getNotes(open.id);
        if (response.status === 200) {
          setNotes(response.data.notes);
          return response.data;
        } else throw new Error();
      } catch (error) {
        toast.error('Error getting note');
      } finally {
        setFetchNote(false);
      }
    };

    if (!!open && open.isHasNote) {
      fetchNote();
    } else if (!open) {
      setNotes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeHandler = () => {
    setSelectedEditNoteId(null);
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          closeHandler();
        }
      }}
      aria-labelledby="view-incident-modal"
      open={!!open}
    >
      <DialogTitle>
        <Typography
          sx={{ fontSize: '22px', fontWeight: 500, lineHeight: '25px' }}
        >
          Notes
        </Typography>
        <IconButton
          aria-label="close"
          onClick={closeHandler}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#212121',
            zIndex: '999',
          })}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {fetchNote ? (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {notes?.map((note) => (
              <Box key={note.id}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '8px',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2" className="subTitle">
                    {note.timestamp}
                  </Typography>

                  {selectedEditNoteId !== note.id && (
                    <Typography
                      variant="body2"
                      className="subTitle"
                      sx={{
                        maxHeight: '24px',
                      }}
                    >
                      {note.created_by}
                      {Number(user.Emp_Code) === Number(note.created_by_id) && (
                        <IconButton
                          sx={{
                            '& .menuItem_img': {
                              width: '18px',
                              height: '18px',
                            },
                            '&:disabled .menuItem_img path': {
                              stroke: '#00000042',
                            },
                          }}
                          onClick={() => {
                            setSelectedEditNoteId(note.id);
                          }}
                        >
                          <EditIcon className="menuItem_img" />
                        </IconButton>
                      )}
                    </Typography>
                  )}
                </Box>
                {selectedEditNoteId === note.id ? (
                  <NoteForm
                    incidentId={open.id}
                    onCancel={closeHandler}
                    onSaved={closeHandler}
                    note={note}
                  />
                ) : (
                  <Typography variant="body1">{note.note}</Typography>
                )}
                {selectedEditNoteId !== note.id && (
                  <Divider
                    sx={{
                      borderColor: 'var(--bs-heading-color);',
                    }}
                  />
                )}
              </Box>
            ))}
            {!selectedEditNoteId && (
              <NoteForm
                incidentId={open?.id}
                onCancel={() => setOpen(null)}
                onSaved={closeHandler}
              />
            )}
          </Box>
        )}
      </DialogContent>
    </BootstrapDialog>
  );
}
